import "react-phone-input-2/lib/bootstrap.css";

import {
  ContractorType,
  DataEntryStepProps,
  FormikInsured,
  PeriodType,
  typedMemo,
} from "src/types";
import { useDataEntryStep2 } from "./data-entry-step-2.hook";
import { FormikErrors, FormikProvider } from "formik";
import { ChangeEvent } from "react";
import { InputDate, InputCheckbox, InputChip, Button } from "src/ui/widgets";
import { NaturalContractor } from "./components/natural-contractor/natural-contractor";
import { LegalContractor } from "./components/legal-contractor/legal-contractor";
import { StrongTranslationString } from "src/ui/widgets/mixed-translation-string/mixed-translation-string";
import { Insured } from "./components/insured/insured";

export const DataEntryStep2 = typedMemo(
  ({ validationSchema, formik }: DataEntryStepProps) => {
    const {
      values,
      errors,
      touched,
      handleChange,
      handleBlur,
      setFieldValue,
      setFieldTouched,
    } = formik;
    const {
      periodOptions,
      coverageStartDateDisabledFrom,
      coverageEndDateDisabledFrom,
      currentDate,
      closePickerTrigger,
      contractorTypeOptions,
      isGroup,
      t,
      openInformativePersonalDataLink,
      onSelectPeriodType,
      onSelectContractorType,
      isContractorNaturalAndInsured,
    } = useDataEntryStep2(formik);

    const PeriodTypeComponent = (
      <div className="col-sm-12 col-lg-6">
        <InputChip
          name="insuredPeriodType"
          handleChange={(event: ChangeEvent<HTMLInputElement>) => {
            onSelectPeriodType(event);
            handleChange(event);
          }}
          handleBlur={handleBlur}
          options={periodOptions}
          validationSchema={validationSchema}
          touched={touched.insuredPeriodType}
          value={values.insuredPeriodType}
          error={errors.insuredPeriodType}
          classes="mb-4"
          label={t("data_entry_step_2.coverage_subtitle")}
          classesLabel="m-0 p-0 font-16-medium d-flex align-items-center mb-3"
        />
      </div>
    );

    const PeriodTypeDates = (
      <div className="col-sm-12 col-md-12 d-block d-md-flex">
        <div className="col-sm-12 col-md-6 p-0 pe-md-3 m-0">
          <InputDate
            name="coverageStartDate"
            handleChange={handleChange}
            handleBlur={handleBlur}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            validationSchema={validationSchema}
            touched={touched.coverageStartDate}
            value={values.coverageStartDate}
            error={errors.coverageStartDate}
            warning={
              // Warning policy for today with only a few hours of validity
              currentDate ? t("data_entry_step_2.alert_end_coverage") : ""
            }
            label={`${t("data_entry.coverage_start_date")}`}
            placeholder={t("data_entry_step_2.coverage_start_date_placeholder")}
            disabledFrom={coverageStartDateDisabledFrom}
            classes="m-0 p-0"
            closePickerTrigger={closePickerTrigger}
          />
        </div>

        {values.insuredPeriodType === PeriodType.PERIOD && (
          <div className="col-sm-12 col-md-6 p-0 ps-md-3 m-0">
            <InputDate
              name="coverageEndDate"
              handleChange={handleChange}
              handleBlur={handleBlur}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              validationSchema={validationSchema}
              touched={touched.coverageEndDate}
              value={values.coverageEndDate}
              error={errors.coverageEndDate}
              label={`${t("data_entry.coverage_end_date")}`}
              placeholder={t("data_entry_step_2.coverage_end_date_placeholder")}
              disabledFrom={coverageEndDateDisabledFrom}
              classes="m-0 p-0"
              closePickerTrigger={closePickerTrigger}
            />
          </div>
        )}
      </div>
    );

    const CoverageData = (
      <div className="data-entry-step-container">
        {PeriodTypeComponent}
        {PeriodTypeDates}
      </div>
    );

    const contractorType = (
      <div className="col-sm-12 col-lg-6 p-0">
        <InputChip
          name="contractorType"
          handleChange={(event: ChangeEvent<HTMLInputElement>) => {
            onSelectContractorType(event);
            handleChange(event);
          }}
          handleBlur={handleBlur}
          options={contractorTypeOptions}
          validationSchema={validationSchema}
          touched={touched.contractorType}
          value={values.contractorType}
          error={errors.contractorType}
          classes="mb-5"
          classesLabel="m-0 p-0 font-16 d-flex align-items-center mb-3"
        />
      </div>
    );

    const Insureds = (
      <div>
        {formik.values.insuredList
          .slice(isContractorNaturalAndInsured() ? 1 : 0)
          .map((_, indexInSlice) => {
            const index = isContractorNaturalAndInsured()
              ? indexInSlice + 1
              : indexInSlice;

            return (
              <div className="data-entry-step-container" key={index}>
                <div className="mb-3 font-18">
                  {t("data_entry_step_2.insured_subtitle")} {index + 1}
                </div>
                <Insured
                  formik={formik}
                  validationSchema={validationSchema}
                  base={`insuredList.${index}.`}
                  insuredValue={formik.values.insuredList?.[index]}
                  insuredTouched={formik.touched.insuredList?.[index] ?? {}}
                  insuredError={
                    formik.errors.insuredList?.[
                      index
                    ] as FormikErrors<FormikInsured>
                  }
                />
              </div>
            );
          })}
      </div>
    );

    const Contractor = (
      <div className="data-entry-step-container">
        <div className="col-sm-12 col-md-6 p-0 pl-md-3 mb-3 font-16-medium text-uppercase">
          {t("data_entry_step_2.contractor_title")}
        </div>
        {!isGroup && <div>{contractorType}</div>}
        <div>
          {values.contractorType === ContractorType.LEGAL && (
            <LegalContractor
              formik={formik}
              validationSchema={validationSchema}
            />
          )}
          {values.contractorType === ContractorType.NATURAL && !isGroup && (
            <NaturalContractor
              formik={formik}
              validationSchema={validationSchema}
            />
          )}
        </div>
      </div>
    );

    const InsuranceConsents = (
      <div className="mt-3">
        <div className="col-sm-12 p-0">
          <div className="d-md-flex flex-column flex-md-row align-items-start justify-content-between">
            <div className="col-md-6 mt-4 mt-md-0">
              <InputCheckbox
                name="consentPrivacy"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={touched.consentPrivacy}
                value={values.consentPrivacy}
                error={
                  errors.consentPrivacy && touched.consentPrivacy
                    ? errors.consentPrivacy
                    : ""
                }
                LabelComponent={
                  <StrongTranslationString i18nKey="data_entry_step_2.consent_privacy_label" />
                }
                classes="d-flex flex-column"
                classesLabel="m-0 font-medium-s d-flex align-items-baseline"
              />

              <div className="d-inline-block ms-4 col-md-11">
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_privacy_text_1")}
                </div>
                <div className="py-md-2 display-contents c-891124 cursor-pointer">
                  <Button
                    flavor="transparentPrimary"
                    text={t("data_entry_step_2.consent_privacy_link")}
                    classes="p-0 display-contents"
                    classesText="font-14-light d-inline border-underline-link-secondary c-secondary"
                    onPress={openInformativePersonalDataLink}
                  />
                </div>
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_privacy_text_2")}
                </div>
              </div>
            </div>

            <div className="col-md-6 mt-5 mt-md-0">
              <InputCheckbox
                name="consentProfiling"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={touched.consentProfiling}
                value={values.consentProfiling}
                label={t("data_entry_step_2.consent_profiling_label")}
                classesLabel="m-0 font-medium-s d-flex align-items-baseline"
              />

              <div className="d-inline-block ms-4">
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_profiling_value")}
                </div>
                <div className="py-md-2 display-contents c-891124 cursor-pointer">
                  <Button
                    flavor="transparentPrimary"
                    text={t("data_entry_step_2.consent_privacy_link_2")}
                    classes="p-0 display-contents"
                    classesText="font-14-light d-inline border-underline-link-secondary c-secondary"
                    onPress={openInformativePersonalDataLink}
                  />
                </div>
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_ending")}
                </div>
              </div>
            </div>
          </div>

          <div className="d-md-flex flex-column flex-md-row align-items-start justify-content-between">
            <div className="col-md-6 mt-5">
              <InputCheckbox
                name="consentMarketing"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={touched.consentMarketing}
                value={values.consentMarketing}
                error={errors.consentMarketing}
                label={t("data_entry_step_2.consent_marketing_label")}
                classesLabel="m-0 font-medium-s d-flex align-items-baseline"
              />

              <div className="d-inline-block ms-4 col-md-11">
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_marketing_value")}
                </div>
                <div className="py-md-2 display-contents c-891124 cursor-pointer">
                  <Button
                    flavor="transparentPrimary"
                    text={t("data_entry_step_2.consent_privacy_link_2")}
                    classes="p-0 display-contents"
                    classesText="font-14-light d-inline border-underline-link-secondary c-secondary"
                    onPress={openInformativePersonalDataLink}
                  />
                </div>
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_ending")}
                </div>
              </div>
            </div>

            <div className="col-md-6 mt-5">
              <InputCheckbox
                name="consentCommunication"
                handleChange={handleChange}
                handleBlur={handleBlur}
                validationSchema={validationSchema}
                touched={touched.consentCommunication}
                value={values.consentCommunication}
                label={t("data_entry_step_2.consent_communication_label")}
                classesLabel="m-0 font-medium-s d-flex align-items-baseline"
              />

              <div className="d-inline-block ms-4">
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_communication_value")}
                </div>
                <div className="py-md-2 display-contents c-891124 cursor-pointer">
                  <Button
                    flavor="transparentPrimary"
                    text={t("data_entry_step_2.consent_privacy_link_2")}
                    classes="p-0 display-contents"
                    classesText="font-14-light d-inline border-underline-link-secondary c-secondary"
                    onPress={openInformativePersonalDataLink}
                  />
                </div>
                <div className="py-md-2 font-14-light display-contents">
                  {t("data_entry_step_2.consent_ending")}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className=" pt-4 pb-4 font-15-semibold">
          {t("commons.required_field")}
        </div>
      </div>
    );

    return (
      <>
        <FormikProvider value={formik}>
          {CoverageData}
          {Contractor}
          {!isGroup && Insureds}
          <div className="mt-3">
            <div className="mb-4 pb-1 border-top" />
          </div>
          {InsuranceConsents}
        </FormikProvider>
      </>
    );
  }
);
